// Financial calculators (pure functions). Saving: contributions at the start of each month, yearly rates
// converted to the equivalent monthly rate. Loans: the usual nominal monthly rate (yearly rate / 12).

const monthlyRate = (yearlyPct) => Math.pow(1 + yearlyPct / 100, 1 / 12) - 1;

/** Value after `years` of a lump sum plus monthly saving at a fixed yearly rate. */
export function growth({ start = 0, monthly = 0, rate = 0, years = 1 }) {
    const n = Math.round(years * 12);
    const r = monthlyRate(rate);
    const g = Math.pow(1 + r, n);
    const final = r === 0 ? start + monthly * n : start * g + monthly * ((g - 1) / r) * (1 + r);
    const contributed = start + monthly * n;
    return { final, contributed, earned: final - contributed };
}

/** Monthly saving needed to reach `target` in `years`, starting from `start`. */
export function goal({ target = 0, start = 0, rate = 0, years = 1 }) {
    const n = Math.round(years * 12);
    const r = monthlyRate(rate);
    const g = Math.pow(1 + r, n);
    const fromStart = start * g;
    if (fromStart >= target) return { monthly: 0, onTrack: true, fromStart };
    const factor = r === 0 ? n : ((g - 1) / r) * (1 + r);
    const monthly = (target - fromStart) / factor;
    return { monthly, onTrack: false, fromStart, contributed: start + monthly * n };
}

/** Fixed-rate loan: monthly payment, total paid and total interest. */
export function loan({ amount = 0, rate = 0, years = 1 }) {
    const n = Math.round(years * 12);
    const i = rate / 100 / 12;
    const payment = i === 0 ? amount / n : (amount * i) / (1 - Math.pow(1 + i, -n));
    return { payment, total: payment * n, interest: payment * n - amount };
}

/** Same plan with two yearly fees: final values and what the higher fee costs. */
export function fees({ start = 0, monthly = 0, rate = 0, feeA = 0, feeB = 0, years = 1 }) {
    const a = growth({ start, monthly, rate: rate - feeA, years });
    const b = growth({ start, monthly, rate: rate - feeB, years });
    return { withA: a.final, withB: b.final, cost: Math.abs(b.final - a.final), cheaper: feeA <= feeB ? 'A' : 'B' };
}
